const SystemCategoriesObject = {
	get systemCategories() {
		// Defensive check: return empty array if store not yet initialized
		if (!this.$store || !this.$store.getters) {
			return [];
		}
		return this.$store.getters.systemCategories || [];
	},

	get subItemCategories() {
		return this.systemCategories.filter((sc) => sc.type.toLocaleLowerCase() === "subitem");
	},

	get topLevelItemCategories() {
		return this.systemCategories.filter((sc) => sc.type.toLocaleLowerCase() === "item");
	},

	get CATEGORIES() {
		return Object.fromEntries(this.systemCategories.map((sc) => [sc.text, sc.systemCategoryId]));
	},

	/**
	 * URL of the category's uploaded icon (versioned, cache-immutable), or null when it has none.
	 * Icons are no longer bundled with the SPA; see SystemCategoryIcon.vue for the built-in fallbacks.
	 */
	getCategoryIcon(systemCategoryId) {
		const category = this.getSystemCategory(systemCategoryId);
		return category?.iconUrl ?? null;
	},

	getSystemCategory(systemCategoryId) {
		return this.systemCategories.find((obj) => obj.systemCategoryId == systemCategoryId);
	},

	getName(systemCategoryId) {
		return this.getSystemCategory(systemCategoryId)?.name;
	},

	load(instance) {
		this.$store = instance.$store;
	},

	isItem(systemCategoryId) {
		return this.topLevelItemCategories.map((sc) => sc.systemCategoryId).includes(systemCategoryId);
	},

	isSubItem(systemCategoryId) {
		return this.subItemCategories.map((sc) => sc.systemCategoryId).includes(systemCategoryId);
	},

	isPackage(systemCategoryId) {
		if (systemCategoryId == null) return false;

		const category = this.systemCategories
			.find((sc) => sc.systemCategoryId == systemCategoryId);

		return category?.type.toLocaleLowerCase() === "package";
	},

	doesNotProcess(systemCategoryId) {
		if (systemCategoryId == null) return false;
		const category = this.getSystemCategory(systemCategoryId);
		return category?.doesNotProcess === true;
	},

	/**
	 * Checks if an item belongs to the "Sample" system category.
	 * Sample items cannot be mixed with non-sample items in the same order.
	 * @param {number} systemCategoryId - The system category ID to check
	 * @returns {boolean} - True if this is a sample item category
	 */
	isSample(systemCategoryId) {
		if (systemCategoryId == null) return false;
		const category = this.getSystemCategory(systemCategoryId);
		return category?.name === "Sample";
	},

	// TODO: This series of allows* functions currently require some hard-coded logic.
	// Ideally, more of these attributes would be pushed into the SystemCategory data model and retrieved from the GQL endpoint.
	// These functions are being added instead of direct calls to the already existing properties and functions so that when the data model is updated,
	// the changes can be made in one place instead of many. E.g. even though allowsSubItems and allowsSizes are currently the same, they may not always be.
	allowsSubItems(systemCategoryId) {
		return this.topLevelItemCategories.map((sc) => sc.systemCategoryId).includes(systemCategoryId);
	},

	allowsSizes(systemCategoryId) {
		return this.topLevelItemCategories.map((sc) => sc.systemCategoryId).includes(systemCategoryId);
	},

	allowsColors(systemCategoryId) {
		return systemCategoryId === null ? false : !this.isPackage(systemCategoryId);
	},

	/**
	 * Sub-item categories the user attaches by hand (as opposed to braids, fabric and
	 * attach items that ride along with the parent). Their style code prints in the
	 * Item # column so the operator can spot them at a glance (#1480).
	 * Matched by name because "Stones" is a client-created category whose ID is not seeded.
	 */
	get manualAttachmentCategoryNames() {
		return ["imprint", "lettering", "monogram", "stone", "stones"];
	},

	isManualAttachmentCategory(systemCategoryId) {
		const name = this.getName(systemCategoryId);
		if (!name) return false;
		return this.manualAttachmentCategoryNames.includes(name.trim().toLocaleLowerCase());
	},

	/**
	 * Determines if a line item should display its style code on prints.
	 * - Regular items (non-sub-items) always show style code
	 * - Manual attachments (Imprint, Lettering, Monogram, Stones sub-items) show style code
	 * - Auto-added sub-items (braids and other categories) hide style code
	 *
	 * @param {Object} lineItem - The line item object with systemCategoryId
	 * @returns {boolean} - True if style code should be shown
	 */
	shouldShowStyleCode(lineItem) {
		if (!lineItem) return false;

		// Show style code if NOT a sub-item
		if (!this.isSubItem(lineItem.systemCategoryId)) {
			return true;
		}

		return this.isManualAttachmentCategory(lineItem.systemCategoryId);
	},
};

export default SystemCategoriesObject;

export const systemCategoriesPlugin = {
	install: function (vue) {
		vue.prototype.$systemCategories = SystemCategoriesObject;
	},
};
